import useAuth from "utils/authorization/authHandling";
import { createContext, useState, useEffect, ReactNode } from "react";
import { logout } from "services/users/accessService";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setInLocalStorage,
} from "utils/localStorage/localStorage.utils";

// Define types for the user session
interface UserContextType {
  user: any | null;
  setUser: React.Dispatch<React.SetStateAction<any | null>>;
  currentVersion: string | null;
  resetVersionAwareness: () => void;
}

// Create the context with default values
export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

// User provider component to manage global session state
export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<any | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  // Call hook at component level (not in callbacks!)
  const { isAuth, Data, loading: isLoading, isLoggedOn } = useAuth();
  // const navigate = useNavigate();

  // Reset version awareness on logout
  const resetVersionAwareness = () => {
    // Clear version-related localStorage entries
    try {
      const user = getFromLocalStorage<any>("user");
      if (user) {
        const id = user?.id || user?.userId;
        if (id) {
          const versionAwareKey = `versionAware_lastSeen_${id}`;
          removeFromLocalStorage(versionAwareKey);
        }
      }
    } catch {
      // Ignore errors
    }
  };

  // Sync user state when auth data changes from the hook
  useEffect(() => {
    // Don't do anything while auth is still loading
    if (isLoading) return;

    try {
      const user = getFromLocalStorage<any>("user");

      // Only logout if no token data AND auth check is complete and failed
      if ((!isLoggedOn && !isAuth) || (!user && !isAuth)) {
        logout();
        return;
      }

      if (user) {
        setUser(user);

        if (isAuth && Data && typeof Data === "object") {
          // Only update if server returns valid user data
          const updatedUserData = Data;
          const updatedUser = { ...user, ...updatedUserData };
          setUser(updatedUser);

          setInLocalStorage("user", updatedUser);
          const newVersion = Data.lastAppVersion || "1.0.0";
          setCurrentVersion(newVersion);
        }
      }
    } catch (error) {
      console.error("Check auth failed:", error);
    }
  }, [isAuth, Data, isLoading]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        currentVersion,
        resetVersionAwareness,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
